import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, TextField, Button, Grid, FormControlLabel, Switch } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import OptionRow from './OptionRow';
import RatingPointRow from './RatingPointRow';
import { Question } from '../../types';

export interface QuestionAdvancedProps {
  question: Question & any;
  qIndex: number;
  setQuestions: React.Dispatch<React.SetStateAction<Question[]>>;
  handleQuestionChange: (index: number, field: keyof Question, value: any) => void;
  handleOptionChange: (questionIndex: number, optionIndex: number, value: string) => void;
  addOption: (questionIndex: number) => void;
  removeOption: (questionIndex: number, optionIndex: number) => void;
  readOnly?: boolean;
}

const QuestionAdvanced: React.FC<QuestionAdvancedProps> = React.memo(({
  question,
  qIndex,
  setQuestions,
  handleQuestionChange,
  handleOptionChange,
  addOption,
  removeOption,
  readOnly,
}) => {
  // Local draft for labels to avoid committing to parent state on each keystroke
  const [labelsDraft, setLabelsDraft] = useState<string[]>(() => {
    const scale = question.ratingScale || 5;
    return Array.from({ length: scale }, (_, i) => question.ratingLabels?.[i] || '');
  });

  // Sync drafts when question or scale changes
  useEffect(() => {
    const scale = question.ratingScale || 5;
    setLabelsDraft(Array.from({ length: scale }, (_, i) => question.ratingLabels?.[i] || ''));
  }, [qIndex, question.ratingScale, question.ratingLabels]);
  const onTypeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    const type = e.target.value as any;
    // normalize fields for type switch
    setQuestions((prev) => {
      const next = [...prev];
      const q = { ...next[qIndex] } as any;
      q.questionType = type;
      if (type === 'multiple_choice') {
        q.options = Array.isArray(q.options) && q.options.length ? q.options : ['', ''];
        q.points = Array.from({ length: q.options.length }, (_, i) => (q.points?.[i] ?? 0));
        delete q.ratingMinLabel;
        delete q.ratingMaxLabel;
        q.ratingScale = undefined;
        q.ratingLabels = [];
        q.allowComment = true; // default-on for multiple choice
      } else if (type === 'rating') {
        q.options = [];
        q.ratingScale = q.ratingScale || 5;
        q.ratingLabels = Array.from({ length: q.ratingScale }, (_, i) => q.ratingLabels?.[i] || '');
        q.points = Array.from({ length: q.ratingScale }, (_, i) => (q.points?.[i] ?? 0));
        q.allowComment = true; // default-on for rating
      } else if (type === 'boolean') {
        q.options = [];
        q.points = Array.from({ length: 2 }, (_, i) => (q.points?.[i] ?? 0));
        q.ratingScale = undefined;
        q.ratingLabels = [];
        delete q.ratingMinLabel;
        delete q.ratingMaxLabel;
        q.allowComment = false;
      } else if (type === 'display') {
        q.options = [];
        q.points = [];
        q.ratingScale = undefined;
        q.ratingLabels = [];
        delete q.ratingMinLabel;
        delete q.ratingMaxLabel;
        q.required = false;
        q.allowComment = false;
      } else {
        // text
        q.options = [];
        q.points = [];
        q.ratingScale = undefined;
        q.ratingLabels = [];
        delete q.ratingMinLabel;
        delete q.ratingMaxLabel;
        q.allowComment = false;
      }
      next[qIndex] = q;
      return next;
    });
  }, [qIndex, setQuestions, readOnly]);

  return (
    <>
      {/* Category */}
      {!readOnly && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
          <TextField
            select
            fullWidth
            label="Category"
            value={(question as any).category || ''}
            SelectProps={{ native: true, displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            size="small"
            onChange={(e) => handleQuestionChange(qIndex, 'category' as any, e.target.value as any)}
          >
            <option value="">None</option>
            <option value="Wellbeing">Wellbeing</option>
            <option value="Culture">Culture</option>
            <option value="Trust">Trust</option>
            <option value="Autonomy">Autonomy</option>
            <option value="Authenticity">Authenticity</option>
            <option value="Resilience">Resilience</option>
            <option value="Collaboration">Collaboration</option>
            <option value="Valuing Difference">Valuing Difference</option>
            <option value="Relationships">Relationships</option>
            <option value="Transparency">Transparency</option>
            <option value="Mission">Mission</option>
          </TextField>
        </Box>
      )}

      {/* Type */}
      {!readOnly && (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 2, width: '100%' }}>
          <TextField
            select
            fullWidth
            label="Question Type"
            value={question.questionType}
            SelectProps={{ native: true }}
            onChange={onTypeChange}
          >
            <option value="text">Text</option>
            <option value="rating">Rating (1-5)</option>
            <option value="multiple_choice">Multiple Choice</option>
            <option value="boolean">Yes/No</option>
            <option value="display">Display (text-only)</option>
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={!!question.allowComment}
                onChange={(e) => handleQuestionChange(qIndex, 'allowComment' as any, e.target.checked as any)}
              />
            }
            label="Allow respondent comment"
            sx={{ alignSelf: 'center' }}
          />
        </Box>
      )}

      {/* Multiple choice */}
      {question.questionType === 'multiple_choice' && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Options</Typography>
          {question.options?.map((option: string, oIndex: number) => (
            <OptionRow
              key={oIndex}
              value={option}
              points={(question as any).points?.[oIndex] ?? ''}
              index={oIndex}
              onChange={(val) => {
                if (readOnly) return;
                handleOptionChange(qIndex, oIndex, val);
              }}
              onPointsChange={(val) => {
                if (readOnly) return;
                setQuestions((prev) => {
                  const next = [...prev];
                  const q = { ...next[qIndex] } as any;
                  const pts = Array.isArray(q.points) ? [...q.points] : [];
                  pts[oIndex] = val;
                  q.points = pts;
                  next[qIndex] = q;
                  return next;
                });
              }}
              onCommitPoints={() => {
                if (readOnly) return;
                setQuestions((prev) => {
                  const next = [...prev];
                  const q = { ...next[qIndex] } as any;
                  const pts = Array.isArray(q.points) ? [...q.points] : [];
                  const v = pts[oIndex];
                  pts[oIndex] = typeof v === 'number' && !isNaN(v as any) ? (v as number) : 0;
                  q.points = pts;
                  next[qIndex] = q;
                  return next;
                });
              }}
              onRemove={() => {
                if (readOnly) return;
                removeOption(qIndex, oIndex);
              }}
            />
          ))}
          <Button startIcon={<AddIcon />} onClick={() => !readOnly && addOption(qIndex)} disabled={readOnly}>Add Option</Button>
        </Box>
      )}

      {/* Rating */}
      {question.questionType === 'rating' && !readOnly && (
        <>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              type="number"
              label="Rating Scale (2-10)"
              value={question.ratingScale || 5}
              onChange={(e) => {
                const val = Math.max(2, Math.min(10, Number(e.target.value) || 5));
                const labels = Array.from({ length: val }, (_, i) => question.ratingLabels?.[i] || '');
                setQuestions((prev) => {
                  const next = [...prev];
                  next[qIndex] = {
                    ...next[qIndex],
                    ratingScale: val,
                    ratingLabels: labels,
                    points: Array.from({ length: val }, (_, i) => (next[qIndex] as any).points?.[i] ?? 0),
                  } as any;
                  return next;
                });
              }}
              inputProps={{ min: 2, max: 10 }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); } }}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Labels per rating</Typography>
            <Grid container spacing={1}>
              {Array.from({ length: question.ratingScale || 5 }).map((_, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <TextField
                    fullWidth
                    label={`Level ${i + 1}`}
                    value={labelsDraft[i] ?? ''}
                    onChange={(e) => {
                      setLabelsDraft((prev) => {
                        const scale = question.ratingScale || 5;
                        const next = Array.from({ length: scale }, (_, j) => prev?.[j] ?? '');
                        next[i] = e.target.value;
                        return next;
                      });
                    }}
                    onBlur={() => {
                      const scale = question.ratingScale || 5;
                      const next = Array.from({ length: scale }, (_, j) => labelsDraft?.[j] || '');
                      handleQuestionChange(qIndex, 'ratingLabels' as any, next as any);
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); } }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Points per rating</Typography>
            <Grid container spacing={1}>
              {Array.from({ length: question.ratingScale || 5 }).map((_, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <RatingPointRow
                    index={i}
                    value={(question as any).points?.[i] ?? ''}
                    onChange={(val) => {
                      setQuestions((prev) => {
                        const next = [...prev];
                        const q = { ...next[qIndex] } as any;
                        const len = q.ratingScale || 5;
                        const pts = Array.isArray(q.points) ? [...q.points] : Array.from({ length: len }, () => 0);
                        (pts as any)[i] = val;
                        q.points = pts;
                        next[qIndex] = q;
                        return next;
                      });
                    }}
                    onCommit={() => {
                      setQuestions((prev) => {
                        const next = [...prev];
                        const q = { ...next[qIndex] } as any;
                        const len = q.ratingScale || 5;
                        const pts = Array.isArray(q.points) ? [...q.points] : Array.from({ length: len }, () => 0);
                        const v = (pts as any)[i];
                        (pts as any)[i] = typeof v === 'number' && !isNaN(v) ? v : 0;
                        q.points = pts;
                        next[qIndex] = q;
                        return next;
                      });
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </>
      )}
    </>
  );
}, (prev, next) => prev.question === next.question && prev.qIndex === next.qIndex);

export default QuestionAdvanced;
